import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { SportPublic, AgeGroupPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function TeamCreatePage() {
  const navigate = useNavigate();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [ageGroupId, setAgeGroupId] = useState("");
  const [sportId, setSportId] = useState("");
  // 161 -- team kind: main (league), trial, or tour (independent, A/B mix)
  const [teamType, setTeamType] = useState<"main" | "trial" | "tour">("main");
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroupPublic[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    api.getSchoolSports(schoolId).then((res) => {
      setSports(res.sports.map((s) => ({ id: s.sport_id, name: s.sport_name, default_court_type_id: null, court_type_name: s.court_type_name ?? null, enabled: true })));
    }).catch(() => {});
  }, [schoolId]);

  // Fetch age groups when sport changes
  useEffect(() => {
    if (!schoolId || !sportId) {
      setAgeGroups([]);
      setAgeGroupId("");
      return;
    }
    api.getAgeGroups(schoolId, sportId)
      .then((res) => {
        setAgeGroups(res.age_groups);
        if (res.age_groups.length > 0) setAgeGroupId(res.age_groups[0].id);
      })
      .catch(() => setAgeGroups([]));
  }, [schoolId, sportId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!schoolId) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.createTeam(schoolId, {
        age_group_id: ageGroupId || undefined,
        sport_id: sportId || undefined,
        team_type: teamType,
      });
      navigate("/teams");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create team");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="team-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Team</h1>
        <p className="text-muted-foreground">Create a new team for this school.</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Team Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="team-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="sport">Sport</Label>
              <select
                id="sport"
                data-field="sport"
                aria-label="Sport"
                value={sportId}
                onChange={(e) => setSportId(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                <option value="">Select a sport</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="age-group">Age Group</Label>
              <select
                id="age-group"
                data-field="age-group"
                aria-label="Age Group"
                value={ageGroupId}
                onChange={(e) => setAgeGroupId(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                disabled={!sportId}
                required
              >
                {!sportId && <option value="">Select a sport first</option>}
                {ageGroups.map((ag) => (
                  <option key={ag.id} value={ag.id}>
                    {ag.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 161 -- team kind. Tour teams are independent of the league: they
                may freely mix A/B players (exempt from the same-event lock) and
                do not affect league standings. */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="team-type">Team kind</Label>
              <select
                id="team-type"
                data-field="team-type"
                aria-label="Team kind"
                value={teamType}
                onChange={(e) => setTeamType(e.target.value as "main" | "trial" | "tour")}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="main">League (main)</option>
                <option value="trial">Trial</option>
                <option value="tour">Tour (independent, A/B mix)</option>
              </select>
              {teamType === "tour" && (
                <p className="text-xs text-muted-foreground">
                  Tour teams are outside the league: free A/B composition, exempt from the same-event selection lock, and excluded from standings.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create Team"}
              </Button>
              <Link to="/teams" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
